import { Card, Col, Row, Skeleton } from 'antd';
import { Bitrix24Consumer, Bitrix24Context } from 'contexts/legacy/Bitrix24Context';
import { LanguageConsumer } from 'contexts/legacy/LanguageContext';
import '../../../styles/insights.css';
import { SystemConsumer } from 'contexts/legacy/SystemContext';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { api } from 'services/api';
import { MessagesData } from 'interfaces/messagesData';
import { MonthPicker } from 'components/Insights/MonthPicker';
import { MessageDataNumbers } from 'components/Insights/MessageDataNumbers';
import { InsightsUnavailable } from 'components/Insights/InsightsUnavailable';
import { TotalMessagesBarChart } from 'components/Insights/Charts/TotalMessagesBarChart';
import { SentAndReceivedLineChart } from 'components/Insights/Charts/SentAndReceivedLineChart';
import { SendingTypesLineChart } from 'components/Insights/Charts/SendingTypesLineChart';
import WidgetDataComponent from 'components/Insights/WidgetDataComponent';
import moment from 'moment';

function InsightsView() {
	const [messagesData, setMessagesData] = useState<MessagesData>();
	const [selectedYear, setSelectedYear] = useState<number | null>(null);
	const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
	const [featureUnavailable, setFeatureUnavailable] = useState<boolean>();
	const bitrix24 = useContext(Bitrix24Context);

	useEffect(() => {
		const auth = bitrix24.infos.auth;
		const domain = bitrix24.infos.domain;

		const fetchData = async () => {
			if (!auth || !domain) {
				return;
			}

			try {
				const { data } = await api.get(`insights/messages_data?auth=${auth}&domain=${domain}`);

				if (data.status) {
					setMessagesData(data.data as MessagesData);
				}

				if (!data.status) {
					setFeatureUnavailable(true);
				}
			} catch (error) {
				console.log('Erorr fetching data', error);
			}
		};

		fetchData();
	}, [bitrix24.infos.auth, bitrix24.infos.domain]);

	if (featureUnavailable) {
		return <InsightsUnavailable />;
	}

	if (!messagesData) {
		return <Skeleton></Skeleton>;
	}

	const getDisabledDates = (pickableDate: moment.Moment | null) => {
		if (!messagesData) {
			return true;
		}

		if (!pickableDate) {
			return true;
		}

		const messageDataYears = Object.keys(messagesData);
		const pickableDateYear = String(pickableDate.year());
		const pickableDateMonth = String(pickableDate.month() + 1);

		if (!messageDataYears.includes(pickableDateYear)) {
			return true;
		}

		const messageDataMonths = Object.keys(messagesData[pickableDateYear]);

		if (!messageDataMonths.includes(pickableDateMonth)) {
			return true;
		}

		return false;
	};

	const getTotalMessages = () => {
		if (!selectedYear || !selectedMonth) {
			let totalMessages = 0;
			for (const year of Object.keys(messagesData)) {
				for (const month of Object.keys(messagesData[year])) {
					totalMessages += messagesData[year][month].sent + messagesData[year][month].received;
				}
			}

			return totalMessages;
		}

		return (
			messagesData[selectedYear][selectedMonth].sent +
			messagesData[selectedYear][selectedMonth].received
		);
	};

	function formatNumberWithDots(number: number) {
		const numberString = String(number);
		return numberString.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	}

	return (
		<div style={{ height: '100vh' }}>
			<Row>
				<Col span={15} className="graphsCol">
					<Row style={{ marginBottom: '10px' }}>
						<Col span={5}>
							<MonthPicker
								getDisabledDates={getDisabledDates}
								setSelectedMonth={setSelectedMonth}
								setSelectedYear={setSelectedYear}
							/>
						</Col>
					</Row>

					<Row gutter={16} style={{ marginBottom: '10px' }}>
						<Col span={8}>
							<div className="entityNumbersRow">
								<Row>
									<Col span={24} className="numbersTitleWrapper">
										<span className="numbersTitle">
											<FormattedMessage id="total_messages" />{' '}
											<span className="messagesTotal">
												{formatNumberWithDots(getTotalMessages())}
											</span>
										</span>
									</Col>
								</Row>
								<Col span={12} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="sent"
									/>
								</Col>
								<Col span={12} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="received"
										letfBorder={true}
									/>
								</Col>
							</div>
						</Col>
						<Col span={16}>
							<div className="entityNumbersRow">
								<Row>
									<Col span={24} className="numbersTitleWrapper">
										<span className="numbersTitle">
											<FormattedMessage id="sendings_by_type" />
										</span>
									</Col>
								</Row>
								<Col span={5} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="crm"
									/>
								</Col>
								<Col span={7} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="automacao"
										letfBorder={true}
									/>
								</Col>
								<Col span={6} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="campanha"
										letfBorder={true}
									/>
								</Col>
								<Col span={6} className="entityNumbers">
									<MessageDataNumbers
										selectedMonth={selectedMonth}
										selectedYear={selectedYear}
										messagesData={messagesData}
										type="chat"
										letfBorder={true}
									/>
								</Col>
							</div>
						</Col>
					</Row>
					<Row gutter={16} style={{ marginBottom: '10px' }}>
						<Col span={12}>
							<Card
								bordered={false}
								style={{
									display: 'flex',
									flexDirection: 'column',
									borderRadius: '10px',
									height: '100%',
								}}
							>
								{messagesData && <SentAndReceivedLineChart messagesData={messagesData} />}
							</Card>
						</Col>
						<Col span={12}>
							<Card
								bordered={false}
								style={{
									display: 'flex',
									flexDirection: 'column',
									borderRadius: '10px',
									height: '100%',
								}}
							>
								{messagesData && <SendingTypesLineChart messagesData={messagesData} />}
							</Card>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							{messagesData && <TotalMessagesBarChart messagesData={messagesData} />}
						</Col>
					</Row>
				</Col>
				<Col span={9} className="tableCol">
					<Row className="widgetTable">
						<WidgetDataComponent />
					</Row>
				</Col>
			</Row>
		</div>
	);
}

export default LanguageConsumer(Bitrix24Consumer(SystemConsumer(InsightsView)));
